import { Button } from '@/components/Button/Button';
import { Chip } from '@/components/Chip/Chip';
import { IconButton } from '@/components/IconButton/IconButton';
import { Icon } from '@/components/Icon/Icon';
import type { Dimension } from '@/graphql/brokerage-settings';
import { outcomeName } from '@/lib/brokerages';

import styles from './DimensionList.module.css';

export interface DimensionListProps {
  title: string;
  /** e.g. "stage", for the button and row labels. */
  singular: string;
  items: readonly Dimension[];
  /** Statuses carry a colour and say whether they end the case or task. */
  hasOutcome: boolean;
  canEdit: boolean;
  /** The row a write is in flight on, whose controls wait. */
  busyId?: string | null;
  onAdd: () => void;
  onEdit: (item: Dimension) => void;
  onDelete: (item: Dimension) => void;
  onMove: (item: Dimension, direction: -1 | 1) => void;
}

/** One ordered list of stages, sources, task types or task statuses. */
export function DimensionList({
  title,
  singular,
  items,
  hasOutcome,
  canEdit,
  busyId = null,
  onAdd,
  onEdit,
  onDelete,
  onMove,
}: DimensionListProps) {
  const sorted = [...items].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  return (
    <section aria-label={title} className={styles.section}>
      <div className={styles.head}>
        <h3 className={styles.title}>
          {title} <span className={styles.count}>{sorted.length}</span>
        </h3>
        {canEdit ? (
          <Button icon={<Icon name="plus" />} onClick={onAdd} size="sm" variant="ghost">
            Add {singular}
          </Button>
        ) : null}
      </div>
      {sorted.length === 0 ? (
        <p className={styles.empty}>None yet.</p>
      ) : (
        <ol className={styles.list}>
          {sorted.map((item, index) => {
            const label = item.label ?? item.key ?? 'Untitled';
            const busy = busyId === item.id;
            const outcome = outcomeName(item);
            return (
              <li className={styles.row} key={item.id}>
                {hasOutcome ? (
                  <span
                    aria-hidden="true"
                    className={styles.swatch}
                    style={{ background: item.color ?? 'var(--surface-sunken)' }}
                  />
                ) : null}
                <span className={styles.main}>
                  <span className={styles.label}>{label}</span>
                  {item.description ? (
                    <span className={styles.meta}>{item.description}</span>
                  ) : null}
                </span>
                {outcome ? (
                  <Chip tone={item.isTerminalSuccess ? 'good' : 'bad'}>{outcome}</Chip>
                ) : (
                  <span />
                )}
                {canEdit ? (
                  <span className={styles.actions}>
                    <IconButton
                      busy={busy}
                      disabled={busy || index === 0}
                      icon="caret-up"
                      label={`Move ${label} up`}
                      onClick={() => onMove(item, -1)}
                    />
                    <IconButton
                      disabled={busy || index === sorted.length - 1}
                      icon="caret-down"
                      label={`Move ${label} down`}
                      onClick={() => onMove(item, 1)}
                    />
                    <IconButton
                      disabled={busy}
                      icon="pencil-simple"
                      label={`Edit ${label}`}
                      onClick={() => onEdit(item)}
                    />
                    <IconButton
                      disabled={busy || sorted.length === 1}
                      icon="trash"
                      label={`Delete ${label}`}
                      onClick={() => onDelete(item)}
                      tone="danger"
                    />
                  </span>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
